class FooterItem extends HTMLElement {
    constructor() {
        super();
        const shadow = this.attachShadow({ mode: 'open' });

        const wrapper = document.createElement('div');
        wrapper.classList.add('footer-item-wrapper');

        shadow.appendChild(wrapper);

        const style = document.createElement('style');
        style.textContent = `
            .footer-item-wrapper {
                border-bottom: 1px solid #d7d7d7;
            }

            .accordion-header {
                cursor: pointer;
                font-weight: 600;
                margin-bottom: 1rem;
                margin-top: 1rem;
                display: flex;
                justify-content: space-between;
                align-items: center;
            }

            .accordion-content {
                display: none;
                overflow: hidden;
                margin-bottom: 1rem;
            }

            .accordion-content.active {
                display: block;
            }

            .icon {
                width: 16px;
                height: 16px;
                transition: transform 0.3s ease;
            }

            .rotate-right {
                animation: rotate-right 0.3s forwards;
            }

            .rotate-left {
                animation: rotate-left 0.3s forwards;
            }

            @keyframes rotate-right {
                from {
                    transform: rotate(0deg);
                }
                to {
                    transform: rotate(180deg);
                }
            }

            @keyframes rotate-left {
                from {
                    transform: rotate(0deg);
                }
                to {
                    transform: rotate(-180deg);
                }
            }

            @media (min-width: 769px) {
                .accordion-header {
                    cursor: default;
                }

                .accordion-content {
                    display: block;
                    margin-bottom: 0;
                }

                .icon {
                    display: none;
                }

                .footer-item-wrapper {
                    border-bottom: none;
                }

                .accordion-header {
                    margin-bottom: 1rem; 
                    margin-top: 0; 
                }
            }
        `;
        shadow.appendChild(style);

        this.toggleAccordion = this.toggleAccordion.bind(this);
        this.checkScreenSize = this.checkScreenSize.bind(this);
    }

    connectedCallback() {
        this.render();
        requestAnimationFrame(() => {
            this.checkScreenSize();
            window.addEventListener('resize', this.checkScreenSize);
        });
    }

    disconnectedCallback() {
        window.removeEventListener('resize', this.checkScreenSize);
    }

    checkScreenSize() {
        const isMobile = window.innerWidth <= 768;
        const content = this.shadowRoot.querySelector('.accordion-content');
        const header = this.shadowRoot.querySelector('.accordion-header');
        const icon = this.shadowRoot.querySelector('.icon');

        if (!content || !header || !icon) return;

        if (isMobile) {
            content.classList.remove('active');
            content.setAttribute('aria-hidden', 'true');
            header.setAttribute('aria-expanded', 'false');
            icon.innerHTML = this.getPlusIcon();
            icon.classList.remove('rotate-left', 'rotate-right');
            header.addEventListener('click', this.toggleAccordion);
            header.addEventListener('keydown', this.handleKeyDown.bind(this));
        } else {
            content.classList.add('active');
            content.setAttribute('aria-hidden', 'false');
            header.setAttribute('aria-expanded', 'true');
            icon.innerHTML = ''; // Icons are hidden on desktop
            icon.classList.remove('rotate-left', 'rotate-right');
            header.removeEventListener('click', this.toggleAccordion);
            header.removeEventListener('keydown', this.handleKeyDown.bind(this));
        }
    }

    toggleAccordion() {
        const content = this.shadowRoot.querySelector('.accordion-content');
        const icon = this.shadowRoot.querySelector('.icon');
        const isActive = content.classList.toggle('active');

        content.setAttribute('aria-hidden', !isActive);
        this.shadowRoot.querySelector('.accordion-header').setAttribute('aria-expanded', isActive);

        icon.innerHTML = isActive ? this.getMinusIcon() : this.getPlusIcon();
        icon.classList.remove('rotate-left', 'rotate-right');
        icon.classList.add(isActive ? 'rotate-right' : 'rotate-left');
    }

    handleKeyDown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.toggleAccordion();
        }
    }

    render() {
        const wrapper = this.shadowRoot.querySelector('.footer-item-wrapper');
        wrapper.innerHTML = ''; // Clear existing content

        const header = document.createElement('div');
        header.classList.add('accordion-header');
        header.setAttribute('aria-expanded', 'false');
        header.setAttribute('aria-controls', 'content');
        header.setAttribute('role', 'button');
        header.textContent = this.getAttribute('header') || 'Footer Item';
        header.setAttribute('tabindex', '0');

        const iconContainer = document.createElement('div');
        iconContainer.classList.add('icon');
        iconContainer.innerHTML = this.getPlusIcon();

        header.appendChild(iconContainer);

        const content = document.createElement('div');
        content.classList.add('accordion-content');
        content.setAttribute('id', 'content');
        content.setAttribute('aria-hidden', 'true');

        const slot = document.createElement('slot');
        content.appendChild(slot);

        wrapper.appendChild(header);
        wrapper.appendChild(content);
    }

    getPlusIcon() {
        return `
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
            </svg>
        `;
    }

    getMinusIcon() {
        return `
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M3.5 8a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 0 1h-8a.5.5 0 0 1-.5-.5z"/>
            </svg>
        `;
    }
}

customElements.define('footer-item', FooterItem);

class ScrollToTopButton extends HTMLElement {
    constructor() {
      super();
  
      // Attach a shadow DOM to the component
      const shadow = this.attachShadow({ mode: 'open' });
  
      // Create a button element
      const button = document.createElement('button');
  
      // Add arrow-up icon (using provided SVG)
      button.innerHTML = `
        <span class="icon">
          <svg focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="m12.0001 6.9394 8.0007 8.0013-1.4142 1.4141L12 9.7678l-6.5869 6.586-1.414-1.4143 8.001-8z"></path>
          </svg>
        </span>
        <span class="text">
          <slot name="button-text">Powrót do góry</slot>
        </span>
      `;
  
      // Style the button
      const style = document.createElement('style');
      style.textContent = `
        button {
          position: absolute;
          bottom: 0;
          right: 100px;  
          width: 40px;
          height: 40px;
          font-size: 14px;
          cursor: pointer;
          background-color: #fff;
          color: #000;
          border: 1px solid #d7d7d7;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-contene: center;
          flex-wrap: nowrap;
          gap: 10px;
          transition: background-color 0.5s, transform 0.5s;
        }
  
        button .text {
          display: none;
          font-size: 14px;
        }
  
        button:hover {
            border: 1px solid #000000;
        }
  
        button:hover .text {
          display: block;
          text-wrap: nowrap;
          background-color: #fff;
          border: 1px solid #000000;

        }
  
        button .icon svg {
          fill: #000;
        }
      `;
  
      // Append the button and styles to the shadow DOM
      shadow.appendChild(style);
      shadow.appendChild(button);
  
      // Add click event to scroll to top
      button.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }
  
  // Register the new element with the browser
  customElements.define('scroll-to-top-button', ScrollToTopButton);

let truncatedDesc = document.querySelector('.description--truncated');
let fullDesc = document.querySelector('.description--full');
let linkMore = document.querySelector('.btn-link--more');
let linkLess = document.querySelector('.btn-link--less');

function showDesc() {
    console.log('click')
    if (fullDesc.classList.contains('hiden')) {
        truncatedDesc.classList.add('hiden')
        fullDesc.classList.remove('hiden')
    } else {
        fullDesc.classList.add('hiden')
        truncatedDesc.classList.remove('hiden')
    }
}

linkMore && linkMore.addEventListener("click", showDesc);
linkLess && linkLess.addEventListener("click", showDesc);

const shadowProductElement = document.querySelector('x-modal');
const testEl = shadowProductElement.shadowRoot.querySelectorAll('slot')[1];
const chatBtnSR = testEl.assignedElements()[0].querySelector('#chat-btn');

function openChat() {
    console.log( 'clicked' )
    const chatEl = document.querySelector('inbox-online-store-chat');
    const chatBtnElSR = chatEl.shadowRoot.querySelector('button')
    chatBtnElSR.click();
}

chatBtnSR.addEventListener('click', openChat)